import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, FormControlLabel, Grid, Icon, makeStyles, Typography } from '@material-ui/core';
import { AxiosError, AxiosResponse } from 'axios';

import NewsData from '../../components/NewsData';
import NewsCategorization from '../../components/NewsCategorization';
import { InfluencerContext} from '../../NewsContext';
import { ErrorStatus } from '../../../../shared/enum/ErrorStatus';
import { AuthContext } from '../../../Authentication/AuthContext';
import { HomeContext } from '../../../Home/HomeContext';
import ClassificationService from '../../../../Services/ClassificationService';
import { ClassificationModel, classificationModelInitial } from '../../../../Models/ClassificationModel';
import { InfluencerModel, newsModelInitial } from '../../../../Models/InfluencerModel';
import { DetachedClassificationAction } from '../../enum/DetachedClassificationAction';

const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    buttonSave: {
        alignItems: 'center'
    },
    gridContainer: {
        marginTop: '8px',
        maxWidth: '1800px',
        padding: theme.spacing(1)
    },
    gridContainerClient: {
        maxWidth: '1800px',
        padding: theme.spacing(3)
    },
}));


export function DetachedClassificationFields() {
    const classes = useStyles();

    const newsContext = React.useContext(InfluencerContext);
    const homeContext = React.useContext(HomeContext);
    const authContext = React.useContext(AuthContext);

    const [hasNotError, setHasNotError] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (newsContext.errorDataStatus == ErrorStatus.HAS_NOT_ERROR && newsContext.errorCategorizationStatus == ErrorStatus.HAS_NOT_ERROR) {
            setHasNotError(true);
        }
        else if ((newsContext.errorDataStatus == ErrorStatus.HAS_ERROR ||
                newsContext.errorCategorizationStatus == ErrorStatus.HAS_ERROR) &&
                newsContext.errorAtLeastOneCategorizationStatus !== ErrorStatus.HAS_ERROR) {
            homeContext.showError('Ops.. existem campos com preenchimento inválido. Por favor, verifique os campos acima e tente novamente.');
        }
    }, [newsContext.errorDataStatus, newsContext.errorCategorizationStatus]);

    React.useEffect(() => {
        if (hasNotError) {
            saveClassification();
            setHasNotError(false);
        }
    }, [hasNotError]);

    const handleClickSave = () => {
        newsContext.setErrorDataStatus(ErrorStatus.RUN);
        newsContext.setErrorCategorizationStatus(ErrorStatus.RUN);
        newsContext.setErrorAtLeastOneCategorizationStatus(ErrorStatus.RUN);
    };

    const saveClassification = () => {
        homeContext.setOpenLoadingDialog(true);

        const classificationData = JSON.parse(JSON.stringify(newsContext.classification));
        classificationData.userCodeMetric = authContext.login.user.codeMetric;
        classificationData.userId = authContext.login.user.id;
        classificationData.vehicle = newsContext.vehicle;
        classificationData.clientCodeMetric = newsContext.news.clientCodeMetric;
        classificationData.endClassification = new Date();
        classificationData.vehicleDictionary = false;

        const clipperId = newsContext.news.clipperId;

        ClassificationService.insertDetachedClassification(classificationData, clipperId)
            .then((res: AxiosResponse) => finishSaveAndClearFields(res.data.object))
            .catch((error: AxiosError) => homeContext.showError('Erro ao salvar classificação. <br/>', error))
            .finally(() => homeContext.setOpenLoadingDialog(false));
    };

    const finishSaveAndClearFields = (classification: ClassificationModel) => {
        const currentNews: InfluencerModel = JSON.parse(JSON.stringify(newsContext.news));
        const currentClassification: ClassificationModel = JSON.parse(JSON.stringify(newsContext.classification));

        if (!newsContext.keepData) {
            const clearNews: InfluencerModel = newsModelInitial;
            const clearClassification: ClassificationModel = classificationModelInitial;
    
            clearNews.clientId = currentNews.clientId;
            clearNews.clientDescription = currentNews.clientDescription;
            clearNews.clientCodeMetric = currentNews.clientCodeMetric;
            clearNews.clipperId = currentNews.clipperId;
            clearClassification.clientId = currentClassification.clientId;
            clearClassification.clientName = currentClassification.clientName;
            clearClassification.clientCodeMetric = currentClassification.clientCodeMetric;
            clearClassification.startClassification = new Date();
    
            newsContext.setNews(clearNews);
            newsContext.setClassification(clearClassification);
            newsContext.setDetachedNewsDataAction(DetachedClassificationAction.CLEAR);
            newsContext.setDetachedNewsCategorizationAction(DetachedClassificationAction.CLEAR);
        }
        
        homeContext.setAlertProps({
            message: getAlertMessage(classification),
            severity: 'success',
            open: true
        });    
    };

    const getAlertMessage = (classification: ClassificationModel) => {
        let idMessage = '';
        if (classification.metricId != '')
            idMessage = `ID Metric: ${classification.metricId}`;
        else
            idMessage = `ID Plataforma: ${classification.id}`;
            
        return (
            <>
                <Typography variant="caption">
                        A classificação da notícia foi salva com sucesso! <br/>
                    <strong>{idMessage}</strong>
                </Typography>
            </>
        );
    };

    const getClassificationTitleAndSubtitle = () => {
        return (
            <>
                <Grid item xs={12} >
                    <Typography variant="h3" >
                            Classificação da notícia
                    </Typography>
                </Grid>
                <Grid container justify="space-between" className={classes.gridContainerClient} spacing={2}>
                    <Typography variant="h4" color="textSecondary">
                        {newsContext.news.clientDescription}
                    </Typography>
                </Grid>
            </>
        );
    };

    const getClassificationNewsData = () => {
        return (
            <Grid
                container
                spacing={3}
                direction="column"
                alignItems="stretch"
                justify="center"
                className={classes.root}>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<Icon className={'material-icons-round'}>expand_more</Icon>}>
                        <Typography variant="h5">Dados da publicação</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <NewsData />
                    </AccordionDetails>
                </Accordion>
            </Grid>
        );
    };

    const getClassificationNewsCategorization = () => {
        return (
            <Grid
                container
                spacing={3}
                direction="column"
                alignItems="stretch"
                justify="center"
                className={classes.root}>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<Icon className={'material-icons-round'}>expand_more</Icon>}>
                        <Typography variant="h5">Categorização da publicação</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <NewsCategorization />
                    </AccordionDetails>
                </Accordion>
            </Grid>
        );
    };

    const getKeepData = () => {
        return (
            <Grid item xs={12}>	
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={newsContext.keepData}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => newsContext.setKeepData(event.target.checked)}
                            name="checkKeepData"
                            color="primary" />
                    }
                    label="Manter dados?" />
            </Grid>
        );
    };

    const getSaveButton = () => {
        return (
            <Grid item>
                <Button 
                    className={classes.buttonSave} 
                    variant="contained" 
                    color="primary" 
                    onClick={handleClickSave} 
                    size="large">
                    SALVAR CLASSIFICAÇÃO
                </Button>
            </Grid>
        );
    };

    return (
        <Grid
            container
            justify="center"
            alignItems="center">
            <Grid
                container
                justify="center"
                className={classes.gridContainer}
                spacing={2}>

                { getClassificationTitleAndSubtitle() }
                { getClassificationNewsData() }
                { getClassificationNewsCategorization() }

                <Grid item>
                    
                    <Grid container 
                        spacing={1}
                        direction="column"
                        alignItems="center"
                        justify="center">
                        { getKeepData() }
                        { getSaveButton() }

                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}